import React, { useEffect, useMemo, useState } from 'react'
import {
  Bell, Check, Eye, EyeOff, History, KeyRound, LayoutDashboard,
  Loader2, Mail, Map as MapIcon, Palette, RotateCcw, Save, Settings, Shield, User
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../config/api'
import { settingsService } from '../services/settingsService'
import type { AppParameter } from '../types/settings'
import { SettingDefinition, SettingValue, useSettings } from '../contexts/SettingsContext'
import { useAuth } from '../contexts/AuthContext'
import { communicationsService } from '../services/communicationsService'
import SiniBrand from './SiniBrand'
import MailCenter from './communications/MailCenter'

type Tab = 'profile' | 'appearance' | 'workspace' | 'notifications' | 'application' | 'mail' | 'system' | 'audit'

interface SettingsCenterProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const FALLBACK_DEFINITIONS: SettingDefinition[] = [
  { key: 'appearance.theme', category: 'Appearance & locale', label: 'Color theme', type: 'ENUM', defaultValue: 'light', personal: true, options: [
    { value: 'light', label: 'Light (default)' }, { value: 'dark', label: 'Dark' }, { value: 'system', label: 'Follow system' }
  ] },
  { key: 'appearance.locale', category: 'Appearance & locale', label: 'Language', type: 'ENUM', defaultValue: 'en', personal: true, options: [
    { value: 'en', label: 'English' }, { value: 'fr', label: 'Français' }
  ] },
  { key: 'appearance.compact_mode', category: 'Appearance & locale', label: 'Compact interface', type: 'BOOLEAN', defaultValue: false, personal: true },
  { key: 'appearance.reduced_motion', category: 'Appearance & locale', label: 'Reduce animations', type: 'BOOLEAN', defaultValue: false, personal: true },
  { key: 'workspace.default_view', category: 'Workspace & map', label: 'Default workspace', type: 'ENUM', defaultValue: 'dashboard', personal: true, options: [
    { value: 'dashboard', label: 'Dashboard' }, { value: 'map', label: 'Map' }, { value: 'vehicles', label: 'Vehicles' },
    { value: 'alerts', label: 'Alerts' }, { value: 'missions', label: 'Missions' }
  ] },
  { key: 'workspace.window_mode', category: 'Workspace & map', label: 'Window mode', type: 'ENUM', defaultValue: 'mac', personal: true, options: [
    { value: 'fixed', label: 'Fixed workspace' }, { value: 'mac', label: 'Mac windows' }
  ] },
  { key: 'workspace.map_style', category: 'Workspace & map', label: 'Map style', type: 'ENUM', defaultValue: 'streets', personal: true, options: [
    { value: 'streets', label: 'Streets' }, { value: 'satellite', label: 'Satellite' }, { value: 'dark', label: 'Dark' }
  ] },
  { key: 'workspace.map_default_zoom', category: 'Workspace & map', label: 'Default map zoom', type: 'INTEGER', defaultValue: 12, personal: true, min: 1, max: 20 },
  { key: 'workspace.cluster_markers', category: 'Workspace & map', label: 'Cluster map markers', type: 'BOOLEAN', defaultValue: true, personal: true },
  { key: 'notifications.in_app', category: 'Notifications', label: 'In-app notifications', type: 'BOOLEAN', defaultValue: true, personal: true },
  { key: 'notifications.email', category: 'Notifications', label: 'Email notifications', type: 'BOOLEAN', defaultValue: true, personal: true },
  { key: 'branding.app_name', category: 'Branding', label: 'Application name', type: 'STRING', defaultValue: 'SiniFleet' },
  { key: 'tracking.live_threshold_minutes', category: 'Tracking', label: 'Live threshold (minutes)', type: 'INTEGER', defaultValue: 60, min: 1, max: 180 },
  { key: 'tracking.stale_threshold_hours', category: 'Tracking', label: 'Stale threshold (hours)', type: 'INTEGER', defaultValue: 24, min: 1, max: 720 },
  { key: 'refresh.dashboard_seconds', category: 'Refresh', label: 'Dashboard refresh (seconds)', type: 'INTEGER', defaultValue: 30, min: 10, max: 300 },
  { key: 'missions.default_priority', category: 'Missions', label: 'Default mission priority', type: 'ENUM', defaultValue: 'MEDIUM', options: [
    { value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }, { value: 'URGENT', label: 'Urgent' }
  ] },
  { key: 'appearance.timezone', category: 'Appearance & locale', label: 'Time zone', type: 'STRING', defaultValue: 'UTC', personal: true },
  { key: 'workspace.map_center_latitude', category: 'Workspace & map', label: 'Map center latitude', type: 'DECIMAL', defaultValue: 14.7167, min: -90, max: 90, personal: true },
  { key: 'workspace.map_center_longitude', category: 'Workspace & map', label: 'Map center longitude', type: 'DECIMAL', defaultValue: -17.4677, min: -180, max: 180, personal: true },
  { key: 'branding.logo_url', category: 'Branding', label: 'Logo URL', type: 'STRING', defaultValue: '' },
  { key: 'branding.default_locale', category: 'Branding', label: 'Default locale', type: 'ENUM', defaultValue: 'en', options: [{ value: 'en', label: 'English' }, { value: 'fr', label: 'Français' }] },
  { key: 'branding.primary_color', category: 'Branding', label: 'Primary color', type: 'STRING', defaultValue: '#2563eb' },
  { key: 'branding.report_legal_line', category: 'Branding', label: 'Report copyright line', type: 'STRING', defaultValue: '(c) {year} Sini Technology - SiniFleet. All rights reserved.' },
  { key: 'refresh.map_seconds', category: 'Refresh intervals', label: 'Map refresh (seconds)', type: 'INTEGER', defaultValue: 30, min: 5, max: 300 },
  { key: 'alerts.default_severity', category: 'Alerts', label: 'Default severity', type: 'ENUM', defaultValue: 'WARNING', options: [
    { value: 'INFO', label: 'Info' }, { value: 'WARNING', label: 'Warning' }, { value: 'CRITICAL', label: 'Critical' }
  ] },
  { key: 'alerts.default_channels', category: 'Alerts', label: 'Default channels', type: 'MULTI_ENUM', defaultValue: ['IN_APP'], options: [
    { value: 'IN_APP', label: 'In app' }, { value: 'EMAIL', label: 'Email' }, { value: 'SMS', label: 'SMS' }
  ] },
  { key: 'alerts.default_recipient_roles', category: 'Alerts', label: 'Default recipient roles', type: 'MULTI_ENUM', defaultValue: ['ADMIN', 'OPERATOR'], options: [
    { value: 'ADMIN', label: 'Admin' }, { value: 'OPERATOR', label: 'Operator' }, { value: 'VIEWER', label: 'Viewer' }
  ] },
  { key: 'alerts.default_cooldown_seconds', category: 'Alerts', label: 'Default cooldown (seconds)', type: 'INTEGER', defaultValue: 300, min: 0, max: 86400 },
  { key: 'missions.default_duration_minutes', category: 'Missions', label: 'Default duration (minutes)', type: 'INTEGER', defaultValue: 480, min: 15, max: 10080 },
  { key: 'features.show_alerts', category: 'Feature visibility', label: 'Show alerts', type: 'BOOLEAN', defaultValue: true },
  { key: 'features.show_missions', category: 'Feature visibility', label: 'Show missions', type: 'BOOLEAN', defaultValue: true },
  { key: 'features.show_staff', category: 'Feature visibility', label: 'Show staff', type: 'BOOLEAN', defaultValue: true },
  { key: 'features.show_geofences', category: 'Feature visibility', label: 'Show geofences', type: 'BOOLEAN', defaultValue: true },
  { key: 'display.max_table_rows', category: 'Display limits', label: 'Maximum table rows', type: 'INTEGER', defaultValue: 100, min: 10, max: 500 },
  { key: 'display.trajectory_max_points', category: 'Display limits', label: 'Maximum trajectory points', type: 'INTEGER', defaultValue: 5000, min: 100, max: 10000 },
  { key: 'retention.audit_days', category: 'Retention', label: 'Settings audit retention (days)', type: 'INTEGER', defaultValue: 365, min: 30, max: 730 },
  { key: 'retention.alert_history_days', category: 'Retention', label: 'Alert history retention (days)', type: 'INTEGER', defaultValue: 90, min: 7, max: 365 }
]

const TAB_META: Array<{ id: Tab; label: string; icon: React.ElementType; admin?: boolean; platformOnly?: boolean }> = [
  { id: 'profile', label: 'My profile', icon: User },
  { id: 'appearance', label: 'Appearance & locale', icon: Palette },
  { id: 'workspace', label: 'Workspace & map', icon: MapIcon },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'application', label: 'Application', icon: Shield, admin: true },
  { id: 'mail', label: 'Mail center', icon: Mail, platformOnly: true },
  { id: 'system', label: 'System parameters', icon: Settings, platformOnly: true },
  { id: 'audit', label: 'Audit', icon: History, admin: true }
]

const categoryForTab = (definition: SettingDefinition, tab: Tab) => {
  const category = definition.category.toLowerCase()
  if (tab === 'appearance') return definition.personal && (category.includes('appearance') || definition.key.startsWith('appearance.'))
  if (tab === 'workspace') return definition.personal && (
    category.includes('workspace') || category.includes('map') ||
    definition.key.startsWith('workspace.') || definition.key.startsWith('map.')
  )
  if (tab === 'notifications') return definition.personal && (
    category.includes('notification') || definition.key.startsWith('notifications.')
  )
  if (tab === 'application') return !definition.personal
  return false
}

const SettingField: React.FC<{
  definition: SettingDefinition
  value: SettingValue
  onChange: (value: SettingValue) => void
}> = ({ definition, value, onChange }) => {
  const id = `setting-${definition.key.replace(/\./g, '-')}`
  const description = definition.description

  if (definition.type === 'BOOLEAN') {
    return (
      <label htmlFor={id} className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 bg-white">
        <span>
          <span className="block text-sm font-medium text-gray-800">{definition.label}</span>
          {description && <span className="block text-[11px] text-gray-500 mt-0.5">{description}</span>}
        </span>
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={value === true}
          onClick={() => onChange(value !== true)}
          className={`relative w-10 h-6 rounded-full transition-colors ${value === true ? 'bg-blue-600' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value === true ? 'translate-x-5' : 'translate-x-1'}`} />
        </button>
      </label>
    )
  }

  if (definition.type === 'MULTI_ENUM') {
    const selected = Array.isArray(value) ? value.map(String) : []
    return (
      <div className="p-3 rounded-xl border border-gray-200 bg-white">
        <div className="text-sm font-medium text-gray-800">{definition.label}</div>
        {description && <div className="text-[11px] text-gray-500 mt-0.5">{description}</div>}
        <div className="flex flex-wrap gap-2 mt-2">
          {(definition.options || []).map(option => (
            <label key={option.value} className="inline-flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={event => onChange(event.target.checked
                  ? [...selected, option.value]
                  : selected.filter(item => item !== option.value))}
                className="rounded border-gray-300 text-blue-600"
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
    )
  }

  return (
    <label htmlFor={id} className="block p-3 rounded-xl border border-gray-200 bg-white">
      <span className="block text-sm font-medium text-gray-800">{definition.label}</span>
      {description && <span className="block text-[11px] text-gray-500 mt-0.5">{description}</span>}
      {definition.type === 'ENUM' ? (
        <select
          id={id}
          value={String(value ?? definition.defaultValue ?? '')}
          onChange={event => onChange(event.target.value)}
          className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
        >
          {(definition.options || []).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      ) : (
        <input
          id={id}
          type={['INTEGER', 'DECIMAL'].includes(definition.type) ? 'number' : 'text'}
          min={definition.min}
          max={definition.max}
          step={definition.type === 'DECIMAL' ? 'any' : undefined}
          value={String(value ?? definition.defaultValue ?? '')}
          onChange={event => onChange(
            ['INTEGER', 'DECIMAL'].includes(definition.type) ? Number(event.target.value) : event.target.value
          )}
          className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
        />
      )}
    </label>
  )
}

const SettingsCenter: React.FC<SettingsCenterProps> = ({ showMessage }) => {
  const { t, i18n } = useTranslation()
  const { user, customerId, refreshUser, isPlatformAdmin, can } = useAuth()
  const { catalog, effective, loading, refresh, savePreferences, saveApplication } = useSettings()
  const isAdmin = can('settings.manage')
  const [tab, setTab] = useState<Tab>('profile')
  const [values, setValues] = useState<Record<string, SettingValue>>({})
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState({ fullName: '', email: '', username: '', role: '', customerName: '' })
  const [savedEmail, setSavedEmail] = useState('')
  const [emailChangePassword, setEmailChangePassword] = useState('')
  const [password, setPassword] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [showPasswords, setShowPasswords] = useState(false)
  const [scope, setScope] = useState<'GLOBAL' | 'CUSTOMER'>(isPlatformAdmin ? 'GLOBAL' : 'CUSTOMER')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(customerId)
  const [customers, setCustomers] = useState<any[]>([])
  const [audit, setAudit] = useState<any[]>([])
  const [systemParameters, setSystemParameters] = useState<AppParameter[]>([])
  const [systemValues, setSystemValues] = useState<Record<string, string>>({})
  const [emailVerification, setEmailVerification] = useState<'unknown' | 'sending' | 'sent' | 'verified'>('unknown')
  const [pendingEmail, setPendingEmail] = useState('')

  const definitions = useMemo(() => {
    if (!catalog.length) return FALLBACK_DEFINITIONS
    const fallbackByKey = new Map(FALLBACK_DEFINITIONS.map(item => [item.key, item]))
    return catalog
      .filter(item => item.key !== 'workspace.layout_preset')
      .map(item => {
        const merged = { ...fallbackByKey.get(item.key), ...item }
        return item.key === 'workspace.window_mode'
          ? { ...merged, defaultValue: 'mac', options: fallbackByKey.get(item.key)?.options }
          : merged
      })
  }, [catalog])

  useEffect(() => setValues(effective), [effective])

  useEffect(() => {
    apiClient.get('/profile').then(response => {
      const row = response.data?.profile || response.data?.data || response.data
      setProfile({
        fullName: row.fullName || row.full_name || '',
        email: row.email || '',
        username: row.username || '',
        role: row.role || '',
        customerName: row.customerName || row.customer_name || ''
      })
      setSavedEmail(row.email || '')
    }).catch(() => setProfile(current => ({
      ...current,
      fullName: user?.fullName || '',
      email: user?.email || '',
      username: user?.username || '',
      role: user?.role || ''
    })))
  }, [user])

  useEffect(() => {
    if (isPlatformAdmin) {
      apiClient.get('/customers').then(response => {
        setCustomers(Array.isArray(response.data) ? response.data : response.data?.customers || [])
      }).catch(() => setCustomers([]))
    }
  }, [isPlatformAdmin])

  useEffect(() => {
    if (tab !== 'application' || !isPlatformAdmin) return
    if (scope === 'CUSTOMER' && !selectedCustomerId) return
    const params = scope === 'CUSTOMER' && selectedCustomerId
      ? { customer_id: selectedCustomerId }
      : undefined
    apiClient.get('/settings/effective', params).then(response => {
      const scopedValues = response.data?.values || {}
      setValues(current => ({ ...current, ...scopedValues }))
    }).catch(() => {})
  }, [isPlatformAdmin, scope, selectedCustomerId, tab])

  useEffect(() => {
    if (tab !== 'audit' || !isAdmin) return
    const params: Record<string, string | number> = { scope: isPlatformAdmin ? scope : 'CUSTOMER' }
    if (scope === 'CUSTOMER' && selectedCustomerId) params.customer_id = selectedCustomerId
    apiClient.get('/settings/audit', params).then(response => {
      setAudit(response.data?.audit || response.data?.data || [])
    }).catch(() => setAudit([]))
  }, [isAdmin, scope, selectedCustomerId, tab])

  useEffect(() => {
    if (tab !== 'system' || !isPlatformAdmin) return
    settingsService.getParameters().then(parameters => {
      setSystemParameters(parameters)
      setSystemValues(Object.fromEntries(parameters.map(parameter => [parameter.key, parameter.value])))
    }).catch(() => {
      setSystemParameters([])
      showMessage?.('Unable to load Balise Management system parameters.', 'error')
    })
  }, [tab, isPlatformAdmin])

  const visibleTabs = TAB_META.filter(item =>
    (!item.admin || isAdmin) && (!item.platformOnly || isPlatformAdmin))
  const fields = definitions.filter(definition => categoryForTab(definition, tab))
  const dirtyValues = Object.fromEntries(fields.map(field => [
    field.key, values[field.key] ?? effective[field.key] ?? field.defaultValue
  ]))

  const saveCurrent = async () => {
    setSaving(true)
    try {
      if (tab === 'profile') {
        const emailChanged = profile.email.trim().toLowerCase() !== savedEmail.trim().toLowerCase()
        if (emailChanged && !emailChangePassword) {
          showMessage?.(t('communications.verification.passwordRequired'), 'warning')
          return
        }
        const response = await apiClient.patch('/profile', {
          fullName: profile.fullName,
          email: profile.email,
          currentPassword: emailChanged ? emailChangePassword : undefined,
          locale: i18n.language?.startsWith('fr') ? 'fr' : 'en'
        })
        if (response.data?.verificationRequired) {
          setPendingEmail(String(response.data.pendingEmail || profile.email))
          setEmailVerification('sent')
        }
        setEmailChangePassword('')
        await refreshUser()
      } else if (['appearance', 'workspace', 'notifications'].includes(tab)) {
        await savePreferences(dirtyValues)
      } else if (tab === 'application') {
        await saveApplication(dirtyValues, scope, scope === 'CUSTOMER' ? selectedCustomerId : null)
      } else if (tab === 'system') {
        const changed = systemParameters.filter(parameter =>
          parameter.editable && systemValues[parameter.key] !== parameter.value)
        await Promise.all(changed.map(parameter =>
          settingsService.updateParameter(parameter.key, systemValues[parameter.key])))
        const refreshed = await settingsService.getParameters()
        setSystemParameters(refreshed)
        setSystemValues(Object.fromEntries(refreshed.map(parameter => [parameter.key, parameter.value])))
      }
      showMessage?.(t('settings_saved', { defaultValue: 'Settings saved' }), 'success')
    } catch (error: any) {
      showMessage?.(error?.message || t('settings_save_failed', { defaultValue: 'Failed to save settings' }), 'error')
    } finally {
      setSaving(false)
    }
  }

  const changePassword = async () => {
    if (password.newPassword.length < 12 || password.newPassword !== password.confirmPassword) {
      showMessage?.('Passwords must match and satisfy the 12-character security policy.', 'warning')
      return
    }
    setSaving(true)
    try {
      await apiClient.post('/profile/change-password', {
        currentPassword: password.currentPassword,
        newPassword: password.newPassword
      })
      setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' })
      showMessage?.('Password changed successfully.', 'success')
    } catch (error: any) {
      showMessage?.(error?.message || 'Failed to change password.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const sendVerificationEmail = async () => {
    if (pendingEmail && !emailChangePassword) {
      showMessage?.(t('communications.verification.passwordRequired'), 'warning')
      return
    }
    setEmailVerification('sending')
    try {
      const locale = i18n.language?.startsWith('fr') ? 'fr' : 'en'
      const response = pendingEmail
        ? await communicationsService.resendEmailChange(emailChangePassword, locale)
        : await communicationsService.requestEmailVerification(locale)
      const alreadyVerified = 'alreadyVerified' in response && response.alreadyVerified === true
      setEmailVerification(alreadyVerified ? 'verified' : 'sent')
      setEmailChangePassword('')
      showMessage?.(t(alreadyVerified
        ? 'communications.verification.alreadyVerified'
        : 'communications.verification.sent'), 'success')
    } catch {
      setEmailVerification('unknown')
      showMessage?.(t('communications.verification.sendFailed'), 'error')
    }
  }

  return (
    <div className="h-full min-h-0 flex bg-slate-50 text-gray-900 overflow-hidden">
      <aside className="w-52 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-100">
          <SiniBrand compact />
          <div className="mt-3 flex items-center gap-2">
            <Settings size={16} className="text-blue-600" />
            <h1 className="text-sm font-semibold">{t('settings_center', { defaultValue: 'Settings Center' })}</h1>
          </div>
          <p className="mt-1 text-[10px] text-gray-500">
            {t('settings_not_monitoring', { defaultValue: 'Profile, preferences and application configuration' })}
          </p>
        </div>
        <nav className="p-2 space-y-1 overflow-y-auto">
          {visibleTabs.map(item => {
            const Icon = item.icon
            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left ${
                  tab === item.id ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon size={14} />
                {t(`settings_tab_${item.id}`, { defaultValue: item.label })}
              </button>
            )
          })}
        </nav>
        <div className="mt-auto p-3 border-t border-gray-100 text-[10px] text-gray-400">
          {user?.role} · {customerId == null ? 'Platform' : `Customer ${customerId}`}
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <div className={`${tab === 'mail' ? 'max-w-7xl' : 'max-w-4xl'} mx-auto p-5`}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-semibold">
                {t(`settings_tab_${tab}`, { defaultValue: visibleTabs.find(item => item.id === tab)?.label })}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {tab === 'application'
                  ? 'Validated runtime parameters. Secrets remain in deployment configuration.'
                  : tab === 'mail' ? t('communications.description')
                  : tab === 'audit' ? 'Recent application-setting changes in your scope.'
                    : 'These settings are independent from System Monitoring.'}
              </p>
            </div>
            {!['audit', 'mail'].includes(tab) && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setValues(effective); refresh() }}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-white"
                  title="Reset unsaved changes"
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  onClick={saveCurrent}
                  disabled={saving || loading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-50"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </div>
            )}
          </div>

          {tab === 'profile' ? (
            <div className="space-y-4">
              <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ['Full name', 'fullName'], ['Email', 'email']
                ].map(([label, key]) => (
                  <label key={key} className="p-3 rounded-xl border border-gray-200 bg-white text-sm">
                    <span className="block text-xs font-medium text-gray-600">{label}</span>
                    <input
                      value={(profile as any)[key]}
                      onChange={event => setProfile(current => ({ ...current, [key]: event.target.value }))}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                    />
                  </label>
                ))}
                {[
                  ['Username', profile.username], ['Role', profile.role], ['Customer', profile.customerName || (customerId == null ? 'Platform' : String(customerId))]
                ].map(([label, value]) => (
                  <div key={label} className="p-3 rounded-xl border border-gray-200 bg-gray-50">
                    <div className="text-xs font-medium text-gray-500">{label}</div>
                    <div className="mt-2 text-sm text-gray-800">{value || '—'}</div>
                  </div>
                ))}
              </section>
              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Mail size={15} className="text-blue-600" />
                      <h3 className="text-sm font-semibold">{t('communications.verification.profileTitle')}</h3>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {emailVerification === 'verified'
                        ? t('communications.verification.profileVerified')
                        : emailVerification === 'sent'
                          ? t('communications.verification.profileSent')
                          : t('communications.verification.profileUnknown')}
                    </p>
                    {pendingEmail && <p className="mt-1 text-[10px] font-medium text-amber-700">
                      {t('communications.verification.pendingEmail', { email: pendingEmail })}
                    </p>}
                    {(pendingEmail || profile.email.trim().toLowerCase() !== savedEmail.trim().toLowerCase()) && (
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={emailChangePassword}
                        onChange={event => setEmailChangePassword(event.target.value)}
                        placeholder={t('communications.verification.currentPassword')}
                        className="mt-3 w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-xs"
                      />
                    )}
                  </div>
                  <button onClick={sendVerificationEmail} disabled={emailVerification === 'sending' || !profile.email}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 disabled:opacity-50">
                    {emailVerification === 'sending' && <Loader2 size={13} className="animate-spin" />}
                    {t(emailVerification === 'sent'
                      ? 'communications.verification.resend'
                      : 'communications.verification.send')}
                  </button>
                </div>
              </section>
              <section className="p-4 rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound size={15} className="text-blue-600" />
                  <h3 className="text-sm font-semibold">Change password</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    ['Current password', 'currentPassword'], ['New password', 'newPassword'], ['Confirm password', 'confirmPassword']
                  ].map(([label, key]) => (
                    <label key={key} className="text-xs text-gray-600">
                      {label}
                      <div className="relative mt-1">
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          value={(password as any)[key]}
                          onChange={event => setPassword(current => ({ ...current, [key]: event.target.value }))}
                          className="w-full px-3 py-2 pr-9 rounded-lg border border-gray-300 text-sm"
                        />
                        <button type="button" onClick={() => setShowPasswords(value => !value)}
                          className="absolute inset-y-0 right-0 px-2 text-gray-400">
                          {showPasswords ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </label>
                  ))}
                </div>
                <button onClick={changePassword} disabled={saving}
                  className="mt-3 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-medium disabled:opacity-50">
                  Update password
                </button>
              </section>
            </div>
          ) : tab === 'mail' ? (
            <MailCenter notify={showMessage} />
          ) : tab === 'application' ? (
            <div className="space-y-4">
              <div className="p-3 rounded-xl border border-blue-100 bg-blue-50 flex flex-wrap items-center gap-3">
                <LayoutDashboard size={15} className="text-blue-600" />
                {isPlatformAdmin ? (
                  <>
                    <select value={scope} onChange={event => setScope(event.target.value as any)}
                      className="px-2 py-1.5 rounded-lg border border-blue-200 text-xs bg-white">
                      <option value="GLOBAL">Global defaults</option>
                      <option value="CUSTOMER">Customer override</option>
                    </select>
                    {scope === 'CUSTOMER' && (
                      <select value={selectedCustomerId || ''} onChange={event => setSelectedCustomerId(Number(event.target.value) || null)}
                        className="px-2 py-1.5 rounded-lg border border-blue-200 text-xs bg-white">
                        <option value="">Select customer…</option>
                        {customers.map(customer => <option key={customer.id} value={customer.id}>{customer.company_name}</option>)}
                      </select>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-blue-800">Customer settings · {customerId}</span>
                )}
              </div>
              {Object.entries(fields.reduce<Record<string, SettingDefinition[]>>((groups, field) => {
                ;(groups[field.category] ||= []).push(field)
                return groups
              }, {})).map(([category, group]) => (
                <section key={category}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{category}</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {group.map(field => <SettingField key={field.key} definition={field}
                      value={values[field.key] ?? effective[field.key] ?? field.defaultValue}
                      onChange={value => setValues(current => ({ ...current, [field.key]: value }))} />)}
                  </div>
                </section>
              ))}
            </div>
          ) : tab === 'system' ? (
            <div className="space-y-5">
              <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-900">
                Global Balise Management parameters. Non-editable identity values remain read-only.
                Security values are labelled by the subsystem that currently enforces them.
              </div>
              {Object.entries(systemParameters.reduce<Record<string, AppParameter[]>>((groups, parameter) => {
                ;(groups[parameter.group_name || 'OTHER'] ||= []).push(parameter)
                return groups
              }, {})).map(([groupName, parameters]) => (
                <section key={groupName}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{groupName}</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {parameters.map(parameter => (
                      <label key={parameter.key} className={`block p-3 rounded-xl border ${
                        parameter.editable ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800">{parameter.key.replace(/_/g, ' ')}</span>
                          {!parameter.editable && <span className="text-[9px] font-bold uppercase text-gray-400">Read only</span>}
                        </span>
                        {parameter.description && <span className="block text-[11px] text-gray-500 mt-0.5">{parameter.description}</span>}
                        {parameter.value_type === 'BOOLEAN' ? (
                          <select disabled={!parameter.editable} value={systemValues[parameter.key] ?? parameter.value}
                            onChange={event => setSystemValues(current => ({ ...current, [parameter.key]: event.target.value }))}
                            className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:bg-gray-100">
                            <option value="true">Enabled</option><option value="false">Disabled</option>
                          </select>
                        ) : (
                          <input disabled={!parameter.editable}
                            type={['INTEGER', 'DECIMAL'].includes(parameter.value_type) ? 'number' : 'text'}
                            value={systemValues[parameter.key] ?? parameter.value}
                            onChange={event => setSystemValues(current => ({ ...current, [parameter.key]: event.target.value }))}
                            className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:bg-gray-100" />
                        )}
                        <span className="block mt-2 text-[10px] text-gray-400">
                          Updated {parameter.updated_at ? new Date(parameter.updated_at).toLocaleString() : '—'}
                          {parameter.updated_by ? ` by ${parameter.updated_by}` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
              {systemParameters.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-400">No system parameters are available.</div>
              )}
            </div>
          ) : tab === 'audit' ? (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              {audit.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-400">No setting changes in this scope.</div>
              ) : audit.map(row => (
                <div key={row.id} className="p-3 border-b border-gray-100 last:border-b-0 flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center"><Check size={12} className="text-blue-600" /></div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium">{row.setting_key || row.key}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {row.updated_by || row.actor} · {new Date(row.created_at || row.changed_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {fields.map(field => <SettingField key={field.key} definition={field}
                value={values[field.key] ?? effective[field.key] ?? field.defaultValue}
                onChange={value => setValues(current => ({ ...current, [field.key]: value }))} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default SettingsCenter
